import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { usePipelineContext } from '../../context';
import type { PipelineStage, PresenceUser } from '../../types';

interface PipelineStageHeaderProps {
  stage: PipelineStage;
  customerCount: number;
  stageValue: number;
  matchCount: number;
  hasSearchMatches: boolean;
  shouldDimStage: boolean;
  stagePresence: PresenceUser[];
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

/**
 * Expanded stage header with full details
 * Shows stage name, value, customer count, search matches, and presence indicators
 */
export function PipelineStageHeader({
  stage,
  customerCount,
  stageValue,
  matchCount,
  hasSearchMatches,
  shouldDimStage,
  stagePresence,
  isCollapsed,
  onToggleCollapse,
}: PipelineStageHeaderProps) {
  const { state } = usePipelineContext();
  const pipelineLayout = state.pipelineLayout;
  const searchActive = state.searchQuery.length > 0;

  return (
    <div className="flex flex-col w-full p-4">
      {/* Top row: Stage name with count + presence + collapse */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="font-bold text-white text-base leading-tight">
            {stage.name}
          </h2>
          <span
            className="stage-metric-badge"
            aria-label={`${customerCount} deals visible`}
          >
            {customerCount}
          </span>
          {searchActive && matchCount > 0 && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-500/90 text-white"
              aria-label={`${matchCount} matching deals`}
            >
              {matchCount} match{matchCount === 1 ? '' : 'es'}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {stagePresence.length > 0 && (
            <div className="flex -space-x-1.5">
              {stagePresence.slice(0, 3).map((person) => (
                <Tooltip key={`stage-presence-${stage.id}-${person.userId}`}>
                  <TooltipTrigger asChild>
                    <div
                      className="stage-presence-avatar"
                      style={{ backgroundColor: person.color }}
                    >
                      {person.initials}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" align="center">
                    {person.name}
                  </TooltipContent>
                </Tooltip>
              ))}
              {stagePresence.length > 3 && (
                <div className="stage-presence-avatar bg-black/30">
                  +{stagePresence.length - 3}
                </div>
              )}
            </div>
          )}
          <button
            type="button"
            className="stage-collapse-btn"
            aria-label={isCollapsed ? 'Expand stage' : 'Collapse stage'}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" aria-hidden />
            ) : (
              <ChevronLeft className="h-4 w-4" aria-hidden />
            )}
          </button>
        </div>
      </div>

      {/* Bottom row: Total value - only show in standard layout */}
      {pipelineLayout === 'standard' && (
        <p className="stage-total-value mt-1">
          £
          {stageValue.toLocaleString('en-GB', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}{' '}
          total
        </p>
      )}
    </div>
  );
}
